import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Calendar, ChevronRight, ExternalLink, Music } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = "/api";

const VenueDirectory = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await axios.get(`${API}/venues/`);
        setVenues(res.data);
      } catch { }
      setLoading(false);
    };
    fetchVenues();
  }, []);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto pb-24 space-y-4">
        <Skeleton className="h-10 w-48" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="venue-directory">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-black mb-1">Twin Cities Venues</h1>
        <p className="text-sm text-muted-foreground mb-6">The best spots for live music in Minneapolis & St. Paul</p>
      </motion.div>

      <div className="space-y-4">
        {venues.map((venue, i) => (
          <motion.div
            key={venue.slug}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
          >
            <Link to={`/venue/${venue.slug}`} className="block group" data-testid={`venue-card-${venue.slug}`}>
              <div className="relative rounded-xl overflow-hidden border border-border bg-card hover:border-purple-500/40 transition-all">
                <div className="flex flex-col sm:flex-row">
                  {/* Image */}
                  <div className="sm:w-48 h-32 sm:h-auto shrink-0 relative overflow-hidden">
                    <img
                      src={venue.cover_image}
                      alt={venue.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={e => { e.target.src = 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50 sm:bg-gradient-to-t sm:from-transparent sm:to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold group-hover:text-purple-400 transition">{venue.name}</h2>
                            {venue.source === 'community' && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-500/20 text-green-400 border border-green-500/30">COMMUNITY</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <MapPin size={11} /> {venue.neighborhood}
                          </p>
                        </div>
                        <ChevronRight size={20} className="text-gray-600 group-hover:text-purple-400 transition shrink-0 mt-1" />
                      </div>
                      <p className="text-xs text-gray-400 mt-2 line-clamp-2">{venue.description}</p>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-xs text-purple-400">
                          <Calendar size={12} /> {venue.upcoming_events} upcoming
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Music size={12} /> {venue.genre_focus?.split(',')[0]}
                        </span>
                      </div>
                      {venue.next_event && (
                        <span className="text-[10px] text-gray-500">
                          Next: {venue.next_event.title?.substring(0, 25)}{venue.next_event.title?.length > 25 ? '...' : ''} ({venue.next_event.date})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 text-center">
        <p className="text-sm text-gray-500 mb-2">Are you a venue in the Twin Cities?</p>
        <Link to="/register/venue"><Button variant="outline" className="border-purple-500/50 text-purple-400">Register Your Venue</Button></Link>
      </motion.div>
    </div>
  );
};

export default VenueDirectory;
